import type { ReactNode } from 'react';
import { ChevronDownIcon, ChevronUpIcon, DragHandleIcon, IconButton, cx } from '@/shared/ui';

export type WidgetShellProps = {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Rendered in the header, before the collapse control. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
};

/**
 * The chrome shared by Interactive Text and Notes: a titled header with a drag
 * handle and a collapse chevron, collapsing to a bare pill.
 *
 * The drag affordance is decorative for now — reordering the two panels is in
 * the design but is not wired up, and a handle that looks draggable and is not
 * is better than a header that silently loses the affordance when it lands.
 */
export function WidgetShell({
  title,
  collapsed,
  onToggle,
  actions,
  className,
  children,
}: WidgetShellProps) {
  return (
    <section className={cx('widget', collapsed && 'is-collapsed', className)} aria-label={title}>
      <header className="widget__head">
        <h3 className="widget__title">{title}</h3>

        <div className="widget__actions">
          {actions}
          <span className="widget__grip" aria-hidden>
            <DragHandleIcon size={16} />
          </span>
          <IconButton
            label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            size="sm"
            variant="ghost"
            aria-expanded={!collapsed}
            onClick={onToggle}
          >
            {collapsed ? <ChevronDownIcon size={16} /> : <ChevronUpIcon size={16} />}
          </IconButton>
        </div>
      </header>

      {collapsed ? null : <div className="widget__body">{children}</div>}
    </section>
  );
}
